import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import {
  TreePine, Leaf, Sparkles, Globe2, Compass, Award, Upload, Search, Share2,
  Camera, MapPin, Waves, Bird, Mountain, Trees, Flower2, Droplets, Wind,
  ChevronRight, Trophy, Medal, QrCode, ShieldCheck, Heart,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import heroImg from "@/assets/verde-hero.webp";
import { OG_CORPORATE_IMAGE } from "@/lib/og";
import mangroveImg from "@/assets/verde-mangrove.webp";
import agaveImg from "@/assets/verde-agave.webp";
import whaleImg from "@/assets/verde-whale.webp";
import pineImg from "@/assets/verde-pine.webp";
import plantingImg from "@/assets/verde-planting.webp";

const verdeSearchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  state: fallback(z.string(), "Todos").default("Todos"),
  climate: fallback(z.string(), "Todos").default("Todos"),
  cats: fallback(z.array(z.string()), []).default([]),
  page: fallback(z.number().int().min(1), 1).default(1),
});

export const Route = createFileRoute("/verde")({
  validateSearch: zodValidator(verdeSearchSchema),
  head: () => ({
    meta: [
      { title: "Mostrando México Verde — Turismo, biodiversidad y conservación" },
      { name: "description", content: "La primera plataforma de turismo, biodiversidad y conservación de México. Descubre. Protege. Reforesta." },
      { property: "og:title", content: "Mostrando México Verde" },
      { property: "og:description", content: "Descubre México. Protege México. $1 USD de cada reserva financia restauración de ecosistemas." },
      { property: "og:image", content: OG_CORPORATE_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_CORPORATE_IMAGE },
    ],
    links: [{ rel: "canonical", href: "https://www.mostrandomexico.mx/verde" }],
  }),
  component: VerdePage,
});


/* ------------------------------ Hooks ------------------------------ */

function useCountUp(target: number, durationMs = 1800, start = true) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return value;
}

function useInView<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.25 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [inView]);
  return { ref, inView };
}

/* ------------------------------ Data ------------------------------ */

const STATES = [
  { code: "CHP", name: "Chiapas", trees: 4280, projects: 7, flora: "Ceiba, Orquídeas", fauna: "Quetzal, Jaguar", eco: "Selva Lacandona" },
  { code: "ROO", name: "Quintana Roo", trees: 3950, projects: 9, flora: "Mangle rojo, Chit", fauna: "Manatí, Jaguar", eco: "Manglares y cenotes" },
  { code: "OAX", name: "Oaxaca", trees: 3120, projects: 6, flora: "Encino, Agave", fauna: "Tortuga golfina", eco: "Sierras y costa" },
  { code: "YUC", name: "Yucatán", trees: 2870, projects: 5, flora: "Ceiba, Henequén", fauna: "Flamenco rosado", eco: "Selva baja y cenotes" },
  { code: "JAL", name: "Jalisco", trees: 2410, projects: 4, flora: "Agave azul, Pino", fauna: "Ballena jorobada", eco: "Sierra y costa" },
  { code: "BCS", name: "Baja California Sur", trees: 1980, projects: 4, flora: "Cardón, Cirio", fauna: "Ballena gris, Lobo marino", eco: "Desierto y mar" },
] as const;

const FLORA = [
  { id: "ceiba", common: "Ceiba", sci: "Ceiba pentandra", state: "Chiapas", climate: "Tropical", tags: ["Árbol", "Endémica"], img: heroImg, status: "Estable" },
  { id: "agave", common: "Agave azul", sci: "Agave tequilana", state: "Jalisco", climate: "Semiárido", tags: ["Endémica", "Ornamental"], img: agaveImg, status: "Cultivada" },
  { id: "mangle", common: "Mangle rojo", sci: "Rhizophora mangle", state: "Quintana Roo", climate: "Costero", tags: ["Árbol"], img: mangroveImg, status: "Protegida" },
  { id: "pino", common: "Pino oaxaqueño", sci: "Pinus oaxacana", state: "Oaxaca", climate: "Templado", tags: ["Árbol", "Endémica"], img: pineImg, status: "Estable" },
  { id: "orquidea-lycaste", common: "Orquídea Lycaste", sci: "Lycaste skinneri", state: "Chiapas", climate: "Húmedo", tags: ["Orquídea", "Ornamental"], img: heroImg, status: "Vulnerable" },
  { id: "nopal", common: "Nopal", sci: "Opuntia ficus-indica", state: "CDMX", climate: "Semiárido", tags: ["Cactácea", "Medicinal"], img: agaveImg, status: "Estable" },
  { id: "cardon", common: "Cardón", sci: "Pachycereus pringlei", state: "Baja California Sur", climate: "Desértico", tags: ["Cactácea", "Endémica"], img: agaveImg, status: "Estable" },
  { id: "cirio", common: "Cirio", sci: "Fouquieria columnaris", state: "Baja California", climate: "Desértico", tags: ["Endémica", "Árbol"], img: agaveImg, status: "Vulnerable" },
  { id: "dalia", common: "Dalia", sci: "Dahlia pinnata", state: "CDMX", climate: "Templado", tags: ["Flor", "Endémica", "Ornamental"], img: heroImg, status: "Estable" },
  { id: "cempasuchil", common: "Cempasúchil", sci: "Tagetes erecta", state: "Puebla", climate: "Templado", tags: ["Flor", "Endémica", "Medicinal", "Ornamental"], img: heroImg, status: "Cultivada" },
  { id: "ahuehuete", common: "Ahuehuete", sci: "Taxodium mucronatum", state: "Estado de México", climate: "Templado", tags: ["Árbol", "Endémica"], img: pineImg, status: "Protegida" },
  { id: "jacaranda", common: "Jacaranda", sci: "Jacaranda mimosifolia", state: "CDMX", climate: "Templado", tags: ["Árbol", "Flor", "Ornamental"], img: heroImg, status: "Cultivada" },
  { id: "vainilla", common: "Vainilla", sci: "Vanilla planifolia", state: "Veracruz", climate: "Tropical", tags: ["Orquídea", "Endémica", "Medicinal"], img: heroImg, status: "Vulnerable" },
  { id: "encina", common: "Encino", sci: "Quercus rugosa", state: "Oaxaca", climate: "Templado", tags: ["Árbol"], img: pineImg, status: "Estable" },
  { id: "chit", common: "Chit", sci: "Thrinax radiata", state: "Quintana Roo", climate: "Costero", tags: ["Árbol", "Endémica"], img: mangroveImg, status: "Protegida" },
  { id: "henequen", common: "Henequén", sci: "Agave fourcroydes", state: "Yucatán", climate: "Semiárido", tags: ["Endémica", "Medicinal"], img: agaveImg, status: "Cultivada" },
  { id: "biznaga", common: "Biznaga dorada", sci: "Echinocactus grusonii", state: "San Luis Potosí", climate: "Semiárido", tags: ["Cactácea", "Endémica", "Ornamental"], img: agaveImg, status: "Vulnerable" },
  { id: "rhynchostele", common: "Orquídea Rhynchostele", sci: "Rhynchostele rossii", state: "Oaxaca", climate: "Húmedo", tags: ["Orquídea", "Endémica"], img: heroImg, status: "Vulnerable" },
  { id: "guayacan", common: "Guayacán", sci: "Guaiacum coulteri", state: "Sinaloa", climate: "Tropical", tags: ["Árbol", "Medicinal"], img: pineImg, status: "Protegida" },
  { id: "damiana", common: "Damiana", sci: "Turnera diffusa", state: "Baja California Sur", climate: "Desértico", tags: ["Medicinal", "Flor"], img: heroImg, status: "Estable" },
  { id: "flor-de-mayo", common: "Flor de mayo", sci: "Plumeria rubra", state: "Yucatán", climate: "Tropical", tags: ["Flor", "Ornamental", "Medicinal"], img: heroImg, status: "Estable" },
  { id: "izote", common: "Izote", sci: "Yucca filifera", state: "Querétaro", climate: "Semiárido", tags: ["Endémica", "Ornamental"], img: agaveImg, status: "Estable" },
  { id: "palo-fierro", common: "Palo fierro", sci: "Olneya tesota", state: "Sonora", climate: "Desértico", tags: ["Árbol", "Endémica"], img: pineImg, status: "Vulnerable" },
  { id: "noche-buena", common: "Nochebuena", sci: "Euphorbia pulcherrima", state: "Morelos", climate: "Templado", tags: ["Flor", "Endémica", "Ornamental"], img: heroImg, status: "Cultivada" },
];

const FLORA_CATEGORIES = ["Endémica", "Medicinal", "Ornamental", "Árbol", "Flor", "Cactácea", "Orquídea"] as const;
const FLORA_STATES = ["Todos", ...Array.from(new Set(FLORA.map((f) => f.state))).sort()];
const FLORA_CLIMATES = ["Todos", ...Array.from(new Set(FLORA.map((f) => f.climate))).sort()];
const FLORA_PAGE_SIZE = 9;

const GREEN_TOURS = [
  { name: "Avistamiento de ballenas — Loreto", cat: "Observación marina", img: whaleImg, impact: "12 árboles / reserva" },
  { name: "Sendero Lacandona", cat: "Senderismo", img: heroImg, impact: "8 árboles / reserva" },
  { name: "Kayak en manglares de Sian Ka'an", cat: "Ecoturismo", img: mangroveImg, impact: "10 árboles / reserva" },
  { name: "Aves endémicas de Oaxaca", cat: "Observación de aves", img: pineImg, impact: "6 árboles / reserva" },
];

const RANKING = [
  { flag: "🇺🇸", country: "Estados Unidos", travelers: 4820, trees: 18240 },
  { flag: "🇲🇽", country: "México", travelers: 3940, trees: 15200 },
  { flag: "🇨🇦", country: "Canadá", travelers: 2110, trees: 8420 },
  { flag: "🇪🇸", country: "España", travelers: 1530, trees: 6120 },
  { flag: "🇩🇪", country: "Alemania", travelers: 980, trees: 3940 },
  { flag: "🇫🇷", country: "Francia", travelers: 870, trees: 3480 },
];

const LEVELS = [
  { name: "Explorador Verde", icon: Compass, min: 1 },
  { name: "Protector Verde", icon: Leaf, min: 10 },
  { name: "Embajador Verde", icon: Sparkles, min: 50 },
  { name: "Guardián de México", icon: ShieldCheck, min: 150 },
  { name: "Leyenda Verde", icon: Trophy, min: 500 },
];

/* ------------------------------ Page ------------------------------ */

import {
  FaunaCatalog, ConservationProjects, CarbonCalculator, Academy, Community, Partners, DonationCTA,
} from "@/components/verde/VerdeSections";

function VerdePage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Hero />
      <ImpactDashboard />
      <InteractiveMap />
      <FloraCatalog />
      <FaunaCatalog />
      <BotanicalAI />
      <ConservationProjects />
      <GreenTourism />
      <CarbonCalculator />
      <TravelAndPlant />
      <Academy />
      <TravelerProfile />
      <Community />
      <GlobalRanking />
      <Certificates />
      <DonationCTA />
      <Partners />
      <FinalCTA />
      <SiteFooter />
    </div>
  );
}


/* ------------------------------ Sections ------------------------------ */

function Hero() {
  const [y, setY] = useState(0);
  useEffect(() => {
    const onScroll = () => setY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <section className="relative h-[100svh] min-h-[640px] overflow-hidden text-white">
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translate3d(0, ${y * 0.3}px, 0) scale(1.08)` }}
      >
        <img decoding="async" fetchPriority="high" src={heroImg} alt="Selva mexicana al amanecer" className="h-full w-full object-cover" width={1920} height={1080} />
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/40 via-emerald-950/30 to-emerald-950/85" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.25),transparent_60%)]" />
      </div>

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-16 sm:px-8 sm:pb-24">
        <div className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] backdrop-blur-md animate-fade-in">
          <TreePine className="h-3.5 w-3.5" /> Mostrando México Verde
        </div>
        <h1 className="mt-6 max-w-4xl font-display text-[clamp(2.5rem,7vw,5.5rem)] font-black leading-[0.95] tracking-tight animate-fade-in">
          Descubre México. <br /><span className="bg-gradient-to-r from-emerald-300 via-teal-200 to-sky-300 bg-clip-text text-transparent">Protege México.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg animate-fade-in">
          Por cada reserva realizada en Mostrando México destinamos <strong>$1 USD</strong> para la restauración y reforestación de ecosistemas mexicanos.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 animate-fade-in">
          <Link to="/tours" className="inline-flex h-12 items-center gap-2 rounded-full bg-emerald-500 px-6 text-sm font-semibold text-emerald-950 shadow-[0_10px_40px_-10px_rgba(16,185,129,0.7)] transition-transform hover:scale-[1.03]">
            Explorar Tours <ChevronRight className="h-4 w-4" />
          </Link>
          <a href="#mapa" className="inline-flex h-12 items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 text-sm font-semibold backdrop-blur-md transition-colors hover:bg-white/20">
            Ver México Verde
          </a>
          <a href="#impacto" className="inline-flex h-12 items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 text-sm font-semibold backdrop-blur-md transition-colors hover:bg-white/20">
            Ver Impacto
          </a>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:max-w-2xl sm:grid-cols-4">
          {[
            { icon: Trees, label: "Selvas" },
            { icon: Waves, label: "Manglares" },
            { icon: Droplets, label: "Cenotes" },
            { icon: Mountain, label: "Bosques" },
          ].map((b) => (
            <div key={b.label} className="flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-3 py-2.5 text-xs font-medium backdrop-blur-md">
              <b.icon className="h-4 w-4 text-emerald-300" /> {b.label}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactStat({ value, label, icon: Icon, suffix = "" }: { value: number; label: string; icon: typeof Leaf; suffix?: string }) {
  const { ref, inView } = useInView<HTMLDivElement>();
  const n = useCountUp(value, 1800, inView);
  return (
    <div ref={ref} className="group relative overflow-hidden rounded-3xl border border-emerald-100 bg-gradient-to-br from-white to-emerald-50/50 p-5 sm:p-6">
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-emerald-500/10 blur-2xl transition-all group-hover:bg-emerald-500/20" />
      <Icon className="h-6 w-6 text-emerald-700" />
      <div className="mt-3 font-display text-3xl font-black tracking-tight text-emerald-950 sm:text-4xl">
        {n.toLocaleString()}{suffix}
      </div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wider text-emerald-700/80">{label}</div>
    </div>
  );
}

function ImpactDashboard() {
  return (
    <section id="impacto" className="relative bg-gradient-to-b from-emerald-950 via-emerald-950 to-emerald-900 py-16 text-white sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-300">
            <Sparkles className="h-3 w-3" /> Impacto en tiempo real
          </div>
          <h2 className="mt-4 font-display text-3xl font-black sm:text-5xl">Cada viaje deja huella verde</h2>
          <p className="mt-3 text-white/70">Resultados acumulados gracias a viajeros como tú.</p>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5">
          <ImpactStat value={42180} label="Árboles financiados" icon={TreePine} />
          <ImpactStat value={1240} label="Hectáreas restauradas" icon={Leaf} />
          <ImpactStat value={18650} label="Viajeros participantes" icon={Heart} />
          <ImpactStat value={24} label="Estados beneficiados" icon={MapPin} />
          <ImpactStat value={3820} label="Ton CO₂ capturadas" icon={Wind} />
        </div>
      </div>
    </section>
  );
}

function InteractiveMap() {
  const [selected, setSelected] = useState<(typeof STATES)[number]>(STATES[0]);
  return (
    <section id="mapa" className="bg-gradient-to-b from-emerald-50/40 to-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <Globe2 className="h-3 w-3" /> Mapa de biodiversidad
          </div>
          <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Explora México por estado</h2>
          <p className="mt-3 text-foreground/70">Selecciona un estado para descubrir su flora, fauna, ecosistemas y proyectos activos de conservación.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
          {/* Map (stylized grid) */}
          <div className="relative overflow-hidden rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-950 to-emerald-800 p-6 text-white">
            <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.4)_1px,transparent_1px)] [background-size:32px_32px]" />
            <div className="relative grid grid-cols-2 gap-3 sm:grid-cols-3">
              {STATES.map((s) => {
                const active = selected.code === s.code;
                return (
                  <button
                    key={s.code}
                    onClick={() => setSelected(s)}
                    className={`group rounded-2xl border p-4 text-left transition-all ${
                      active
                        ? "border-emerald-300 bg-emerald-500/20 shadow-[0_0_0_3px_rgba(16,185,129,0.25)]"
                        : "border-white/15 bg-white/5 hover:border-emerald-300/60 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-display text-lg font-black">{s.name}</span>
                      <span className="rounded-full bg-emerald-400/20 px-2 py-0.5 text-[10px] font-bold text-emerald-200">{s.code}</span>
                    </div>
                    <div className="mt-3 text-xs text-white/70">{s.eco}</div>
                    <div className="mt-3 flex items-center justify-between text-[11px]">
                      <span className="text-emerald-300">🌳 {s.trees.toLocaleString()}</span>
                      <span className="text-white/60">{s.projects} proyectos</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          <div key={selected.code} className="animate-fade-in rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Estado destacado</div>
            <h3 className="mt-1 font-display text-3xl font-black text-emerald-950">{selected.name}</h3>
            <p className="mt-2 text-sm text-foreground/70">{selected.eco}</p>

            <div className="mt-5 space-y-3 text-sm">
              <DetailRow icon={Flower2} label="Flora" value={selected.flora} />
              <DetailRow icon={Bird} label="Fauna" value={selected.fauna} />
              <DetailRow icon={TreePine} label="Árboles financiados" value={selected.trees.toLocaleString()} />
              <DetailRow icon={ShieldCheck} label="Proyectos activos" value={`${selected.projects} en curso`} />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/tours" className="inline-flex h-10 items-center gap-1.5 rounded-full bg-emerald-600 px-4 text-xs font-semibold text-white hover:bg-emerald-700">
                Ver tours <ChevronRight className="h-3.5 w-3.5" />
              </Link>
              <button className="inline-flex h-10 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-4 text-xs font-semibold text-emerald-800 hover:bg-emerald-100">
                Apoyar reforestación
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: typeof Leaf; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-emerald-50/60 p-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">{label}</div>
        <div className="text-sm text-emerald-950">{value}</div>
      </div>
    </div>
  );
}

type VerdeSearch = z.infer<typeof verdeSearchSchema>;

function FloraCatalog() {
  const search = Route.useSearch() as VerdeSearch;
  const { q, state: stateF, climate: climateF, cats, page } = search;
  const navigate = useNavigate({ from: "/verde" });

  function update(patch: Partial<VerdeSearch>) {
    navigate({
      search: (prev: VerdeSearch): VerdeSearch => {
        const next: VerdeSearch = { ...prev, ...patch };
        if (patch.page === undefined && (patch.q !== undefined || patch.state !== undefined || patch.climate !== undefined || patch.cats !== undefined)) {
          next.page = 1;
        }
        return next;
      },
      replace: true,
    });
  }

  const setQ = (v: string) => update({ q: v });
  const setStateF = (v: string) => update({ state: v });
  const setClimateF = (v: string) => update({ climate: v });
  const setPage = (v: number) => update({ page: v });

  function toggleCat(c: string) {
    update({ cats: cats.includes(c) ? cats.filter((x: string) => x !== c) : [...cats, c] });
  }

  function resetFilters() {
    navigate({ search: { q: "", state: "Todos", climate: "Todos", cats: [], page: 1 }, replace: true });
  }



  const term = q.trim().toLowerCase();
  const filtered = FLORA.filter((f) => {
    const matchQ = !term || f.common.toLowerCase().includes(term) || f.sci.toLowerCase().includes(term) || f.state.toLowerCase().includes(term);
    const matchState = stateF === "Todos" || f.state === stateF;
    const matchClimate = climateF === "Todos" || f.climate === climateF;
    const matchCats = cats.length === 0 || cats.every((c) => f.tags.includes(c));
    return matchQ && matchState && matchClimate && matchCats;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / FLORA_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * FLORA_PAGE_SIZE, currentPage * FLORA_PAGE_SIZE);
  const activeCount = (stateF !== "Todos" ? 1 : 0) + (climateF !== "Todos" ? 1 : 0) + cats.length + (term ? 1 : 0);

  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-8 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <Flower2 className="h-3 w-3" /> México Verde — Flora
          </div>
          <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Enciclopedia de plantas mexicanas</h2>
          <p className="mt-3 text-foreground/70">Más de 25,000 especies. Buscador avanzado, fichas científicas y mapas de distribución.</p>
        </div>

        {/* Advanced search bar */}
        <div className="mb-5 rounded-3xl border border-emerald-100 bg-emerald-50/50 p-4 sm:p-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_auto_auto_auto]">
            <div className="flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2.5">
              <Search className="h-4 w-4 text-emerald-700" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Busca por nombre común, científico o estado"
                className="w-full bg-transparent text-sm outline-none placeholder:text-emerald-700/50"
                aria-label="Buscar flora"
              />
            </div>
            <select
              value={stateF}
              onChange={(e) => { setStateF(e.target.value); setPage(1); }}
              className="rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm text-emerald-900 outline-none focus:border-emerald-500"
              aria-label="Filtrar por estado"
            >
              {FLORA_STATES.map((s) => (
                <option key={s} value={s}>{s === "Todos" ? "Estado: todos" : s}</option>
              ))}
            </select>
            <select
              value={climateF}
              onChange={(e) => { setClimateF(e.target.value); setPage(1); }}
              className="rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm text-emerald-900 outline-none focus:border-emerald-500"
              aria-label="Filtrar por clima"
            >
              {FLORA_CLIMATES.map((c) => (
                <option key={c} value={c}>{c === "Todos" ? "Clima: todos" : c}</option>
              ))}
            </select>
            <button
              onClick={resetFilters}
              disabled={activeCount === 0}
              className="rounded-full border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Limpiar {activeCount > 0 ? `(${activeCount})` : ""}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {FLORA_CATEGORIES.map((c) => {
              const active = cats.includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleCat(c)}
                  aria-pressed={active}
                  className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                    active ? "border-emerald-700 bg-emerald-700 text-white" : "border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between text-sm text-emerald-900/70">
          <span><strong className="text-emerald-950">{filtered.length}</strong> especies encontradas</span>
          {totalPages > 1 && <span>Página {currentPage} de {totalPages}</span>}
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {pageItems.map((f) => (
            <article key={f.id} className="group overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={f.img} alt={f.common} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                  <Leaf className="h-3 w-3" /> {f.status}
                </div>
                <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-900/80 px-2.5 py-1 text-[10px] font-semibold text-white backdrop-blur">
                  {f.climate}
                </div>
              </div>
              <div className="p-5">
                <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">{f.state}</div>
                <h3 className="mt-1 font-display text-xl font-black text-emerald-950">{f.common}</h3>
                <p className="text-xs italic text-foreground/60">{f.sci}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {f.tags.map((t) => (
                    <span key={t} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">{t}</span>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-emerald-100 pt-4 text-xs">
                  <Link to="/tours" className="font-semibold text-emerald-700 hover:underline">Tours relacionados →</Link>
                  <div className="flex gap-1">
                    <button title="Compartir" className="grid h-8 w-8 place-items-center rounded-full hover:bg-emerald-50"><Share2 className="h-3.5 w-3.5 text-emerald-700" /></button>
                    <button title="Reforestar" className="grid h-8 w-8 place-items-center rounded-full hover:bg-emerald-50"><TreePine className="h-3.5 w-3.5 text-emerald-700" /></button>
                  </div>
                </div>
              </div>
            </article>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full rounded-3xl border border-dashed border-emerald-200 p-10 text-center text-sm text-foreground/60">
              No encontramos especies para esos filtros. <button onClick={resetFilters} className="font-semibold text-emerald-700 hover:underline">Limpiar filtros</button>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <nav className="mt-10 flex items-center justify-center gap-2" aria-label="Paginación">
            <button
              onClick={() => setPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ← Anterior
            </button>
            <div className="flex flex-wrap items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  aria-current={p === currentPage ? "page" : undefined}
                  className={`h-9 min-w-9 rounded-full px-3 text-sm font-semibold transition-colors ${
                    p === currentPage ? "bg-emerald-700 text-white" : "border border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <button
              onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Siguiente →
            </button>
          </nav>
        )}
      </div>
    </section>
  );
}

function BotanicalAI() {
  const [step, setStep] = useState<"idle" | "loading" | "result">("idle");
  const [preview, setPreview] = useState<string | null>(null);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setStep("loading");
    setTimeout(() => setStep("result"), 1600);
  }

  return (
    <section className="bg-gradient-to-b from-emerald-950 to-emerald-900 py-16 text-white sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-300">
            <Sparkles className="h-3 w-3" /> IA Botánica
          </div>
          <h2 className="mt-4 font-display text-3xl font-black sm:text-5xl">Identifica cualquier planta de México</h2>
          <p className="mt-3 text-white/70">Sube una fotografía y nuestra IA te dirá qué especie es, cómo cuidarla y por qué importa para el ecosistema.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
            <label className="flex aspect-[4/3] cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-emerald-300/40 bg-emerald-900/40 transition-colors hover:border-emerald-300 hover:bg-emerald-900/60">
              {preview ? (
                <img loading="lazy" decoding="async" src={preview} alt="preview" className="h-full w-full rounded-2xl object-cover" />
              ) : (
                <>
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-500/20">
                    <Camera className="h-6 w-6 text-emerald-300" />
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">Sube una foto</div>
                    <div className="text-xs text-white/60">JPG / PNG · max 10MB</div>
                  </div>
                </>
              )}
              <input type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
            <button onClick={() => { setPreview(null); setStep("idle"); }} className="mt-4 inline-flex h-10 items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 text-xs font-semibold hover:bg-white/10">
              <Upload className="h-3.5 w-3.5" /> Limpiar
            </button>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
            {step === "idle" && (
              <div className="grid h-full place-items-center text-center text-white/60">
                <div>
                  <Sparkles className="mx-auto h-8 w-8 text-emerald-300" />
                  <p className="mt-3 max-w-xs text-sm">Sube una foto para comenzar el análisis botánico.</p>
                </div>
              </div>
            )}
            {step === "loading" && (
              <div className="grid h-full place-items-center text-center">
                <div>
                  <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-emerald-300 border-t-transparent" />
                  <p className="mt-4 text-sm text-white/80">Analizando hojas, forma y patrones…</p>
                </div>
              </div>
            )}
            {step === "result" && (
              <div className="animate-fade-in">
                <div className="text-xs font-semibold uppercase tracking-widest text-emerald-300">Identificación</div>
                <h3 className="mt-1 font-display text-3xl font-black">Agave azul</h3>
                <p className="text-sm italic text-white/60">Agave tequilana</p>
                <div className="mt-5 space-y-3 text-sm">
                  <p><strong className="text-emerald-300">Cuidados:</strong> Suelo bien drenado, sol pleno, riego moderado. Resistente a sequía.</p>
                  <p><strong className="text-emerald-300">Distribución:</strong> Jalisco, Nayarit, Guanajuato, Michoacán.</p>
                  <p><strong className="text-emerald-300">Importancia ecológica:</strong> Refugio de murciélagos polinizadores. Símbolo cultural de México.</p>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Link to="/tours" className="inline-flex h-10 items-center gap-1.5 rounded-full bg-emerald-500 px-4 text-xs font-semibold text-emerald-950 hover:bg-emerald-400">Tours relacionados</Link>
                  <button className="inline-flex h-10 items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-4 text-xs font-semibold hover:bg-white/10">Ver ficha completa</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function GreenTourism() {
  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
              <Trees className="h-3 w-3" /> Turismo Verde
            </div>
            <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Viaja con propósito</h2>
            <p className="mt-3 text-foreground/70">Ecoturismo, observación de aves, manglares, senderismo y reservas naturales. Cada reserva siembra árboles.</p>
          </div>
          <Link to="/tours" className="inline-flex h-11 items-center gap-2 self-start rounded-full bg-emerald-700 px-5 text-sm font-semibold text-white hover:bg-emerald-800">
            Ver todos los tours <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {GREEN_TOURS.map((t) => (
            <article key={t.name} className="group overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={t.img} alt={t.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-emerald-950 via-emerald-950/60 to-transparent p-4">
                  <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-950">
                    🌳 Contribuye a la restauración
                  </div>
                  <div className="mt-2 text-[11px] font-semibold text-emerald-300">{t.cat}</div>
                  <h3 className="font-display text-lg font-black leading-tight text-white">{t.name}</h3>
                  <div className="mt-1 text-[11px] text-white/75">{t.impact}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function TravelAndPlant() {
  return (
    <section className="relative overflow-hidden bg-emerald-50/60 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
              <TreePine className="h-3 w-3" /> Viaja y Siembra
            </div>
            <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Tu viaje también planta vida</h2>
            <p className="mt-4 text-foreground/75">Participa en jornadas de reforestación, restauración de manglares y conservación de selvas. Tu mano hace la diferencia.</p>

            <ul className="mt-6 space-y-3">
              {[
                "Reforestación comunitaria con especies nativas",
                "Restauración de manglares en Yucatán y Quintana Roo",
                "Conservación de la Selva Lacandona",
                "Liberación de tortugas en Oaxaca",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm">
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-100"><Leaf className="h-4 w-4 text-emerald-700" /></div>
                  <span className="text-sm text-foreground/85">{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 rounded-3xl border border-emerald-200 bg-white p-5">
              <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Testimonio</div>
              <p className="mt-2 font-display text-lg leading-snug text-emerald-950">
                "Plantamos 40 árboles en Tulum. Volveré cada año."
              </p>
              <p className="mt-2 text-xs text-foreground/60">— Ana M., viajera embajadora verde</p>
            </div>
          </div>

          <div className="relative">
            <img src={plantingImg} alt="Viajeros sembrando árboles" loading="lazy" className="aspect-[4/5] w-full rounded-3xl object-cover shadow-2xl" />
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-emerald-200 bg-white p-4 shadow-xl sm:block">
              <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Próximo evento</div>
              <div className="mt-1 font-display text-lg font-black text-emerald-950">Reforestación Selva Maya</div>
              <div className="mt-1 text-xs text-foreground/60">Sáb 14 de marzo · Cobá</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TravelerProfile() {
  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <Medal className="h-3 w-3" /> Perfil del Viajero
          </div>
          <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Tu impacto, en niveles</h2>
          <p className="mt-3 text-foreground/70">Cada reserva, cada árbol y cada estado apoyado suma a tu legado verde.</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
          <div className="overflow-hidden rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-700 to-emerald-900 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white/15 backdrop-blur"><Sparkles className="h-7 w-7 text-emerald-300" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-emerald-300">Nivel actual</div>
                <div className="font-display text-2xl font-black">Embajador Verde</div>
              </div>
            </div>
            <div className="mt-6 space-y-3 text-sm">
              <Row label="Árboles financiados" value="64" />
              <Row label="Estados apoyados" value="5" />
              <Row label="Tours realizados" value="9" />
              <Row label="Insignias" value="7" />
            </div>
            <div className="mt-6">
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="text-white/70">Progreso a Guardián</span>
                <span className="font-semibold">64 / 150</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/15">
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-300 to-teal-300" style={{ width: "42%" }} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {LEVELS.map((lvl) => (
              <div key={lvl.name} className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4 text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-emerald-700 text-white">
                  <lvl.icon className="h-5 w-5" />
                </div>
                <div className="mt-3 text-xs font-bold text-emerald-950">{lvl.name}</div>
                <div className="mt-1 text-[10px] uppercase tracking-wider text-emerald-700/80">{lvl.min}+ árboles</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-2.5 backdrop-blur">
      <span className="text-white/75">{label}</span>
      <span className="font-display text-lg font-black">{value}</span>
    </div>
  );
}

function GlobalRanking() {
  return (
    <section className="bg-emerald-50/40 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <Trophy className="h-3 w-3" /> Ranking Global
          </div>
          <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Quién ayuda más a México</h2>
          <p className="mt-3 text-foreground/70">Países que más contribuyen a la restauración de ecosistemas mexicanos.</p>
        </div>

        <div className="overflow-hidden rounded-3xl border border-emerald-100 bg-white shadow-sm">
          <div className="grid grid-cols-[40px_1fr_auto_auto] gap-3 border-b border-emerald-100 bg-emerald-50/60 px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-emerald-700 sm:grid-cols-[40px_1fr_120px_140px] sm:px-6">
            <span>#</span>
            <span>País</span>
            <span className="text-right">Viajeros</span>
            <span className="text-right">Árboles</span>
          </div>
          {RANKING.map((r, i) => (
            <div key={r.country} className="grid grid-cols-[40px_1fr_auto_auto] items-center gap-3 border-b border-emerald-100/70 px-4 py-4 text-sm last:border-b-0 sm:grid-cols-[40px_1fr_120px_140px] sm:px-6">
              <span className="font-display text-lg font-black text-emerald-700">{i + 1}</span>
              <span className="flex items-center gap-3 font-semibold text-emerald-950"><span className="text-xl">{r.flag}</span>{r.country}</span>
              <span className="text-right font-medium tabular-nums text-foreground/75">{r.travelers.toLocaleString()}</span>
              <span className="text-right font-display text-base font-black tabular-nums text-emerald-700">{r.trees.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Certificates() {
  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-[1fr_500px] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-emerald-800">
              <Award className="h-3 w-3" /> Certificados digitales
            </div>
            <h2 className="mt-4 font-display text-3xl font-black text-emerald-950 sm:text-5xl">Tu huella verde, certificada</h2>
            <p className="mt-3 text-foreground/70">Después de cada compra recibes un certificado digital con tus árboles financiados, el proyecto apoyado y un código QR verificable.</p>

            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                { icon: QrCode, t: "QR verificable" },
                { icon: TreePine, t: "Árboles financiados" },
                { icon: MapPin, t: "Proyecto apoyado" },
                { icon: ShieldCheck, t: "Firma blockchain" },
              ].map((x) => (
                <li key={x.t} className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/40 p-3">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-700 text-white"><x.icon className="h-4 w-4" /></div>
                  <span className="text-sm font-semibold text-emerald-950">{x.t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Certificate mock */}
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-100 p-8 shadow-2xl">
              <div className="flex items-center justify-between">
                <div className="font-display text-xs font-bold uppercase tracking-[0.3em] text-emerald-700">Certificado Verde</div>
                <TreePine className="h-6 w-6 text-emerald-700" />
              </div>
              <div className="mt-6">
                <div className="text-xs text-emerald-700/70">Gracias por ayudar a restaurar México</div>
                <div className="mt-2 font-display text-3xl font-black text-emerald-950">Ana Martínez</div>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="font-semibold uppercase tracking-widest text-emerald-700">Árboles</div>
                  <div className="mt-1 font-display text-2xl font-black text-emerald-950">12</div>
                </div>
                <div>
                  <div className="font-semibold uppercase tracking-widest text-emerald-700">Fecha</div>
                  <div className="mt-1 font-display text-lg font-black text-emerald-950">14 · 03 · 2026</div>
                </div>
                <div className="col-span-2">
                  <div className="font-semibold uppercase tracking-widest text-emerald-700">Proyecto</div>
                  <div className="mt-1 font-display text-lg font-black text-emerald-950">Selva Maya — Cobá</div>
                </div>
              </div>

              <div className="mt-6 flex items-end justify-between">
                <div className="grid h-20 w-20 place-items-center rounded-xl bg-emerald-950 text-white">
                  <QrCode className="h-12 w-12" />
                </div>
                <div className="text-right">
                  <div className="font-display text-xs italic text-emerald-700">Mostrando México Verde</div>
                  <div className="text-[10px] text-emerald-700/70">ID #MMV-2026-0042</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-emerald-950 py-20 text-white">
      <div className="absolute inset-0 opacity-30">
        <img src={heroImg} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/85 to-emerald-950/60" />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <h2 className="font-display text-4xl font-black leading-tight sm:text-6xl">
          El futuro del turismo en México es <span className="bg-gradient-to-r from-emerald-300 to-teal-200 bg-clip-text text-transparent">verde</span>.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-base text-white/75 sm:text-lg">
          Únete a miles de viajeros que ya están restaurando ecosistemas con cada experiencia que viven.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/tours" className="inline-flex h-12 items-center gap-2 rounded-full bg-emerald-500 px-6 text-sm font-bold text-emerald-950 shadow-[0_10px_40px_-10px_rgba(16,185,129,0.7)] hover:scale-[1.03]">
            Reservar mi primera experiencia <ChevronRight className="h-4 w-4" />
          </Link>
          <a href="#mapa" className="inline-flex h-12 items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 text-sm font-semibold backdrop-blur-md hover:bg-white/20">
            Explorar México Verde
          </a>
        </div>
      </div>
    </section>
  );
}
